import languages from './languages';
import type { Lucid } from '../../types/types';

/** Locates the correct language object from a potential languageId */
const getLanguageData = (
  languageId: string,
  key: 'i18nKey' | 'shortLangPrefix' | 'languageCode',
): Lucid.I18n.Language => {
  const [currentLanguageKey] = (Object.keys(languages) as Array<Lucid.I18n.AvailableLanguagesKey>).filter(
    languageKey => languageId.toLowerCase() === languages[languageKey][key].toLowerCase(),
  );

  return languages[currentLanguageKey];
};

/** Checks if the languageId supplied matches a key on the languages object. */
const isLanguageKey = (languageId: string): languageId is Lucid.I18n.AvailableLanguagesKey => languageId in languages;

/** Checks if the languageId supplied is an i18n mapped key on the languages object. */
const isLanguageI18nKey = (languageId: string): languageId is Lucid.I18n.AvailableLanguagesI18nKey => {
  const languageKeys = getLanguageData(languageId, 'i18nKey') || {};

  return languageKeys && !!Object.keys(languageKeys).length;
};

/** Checks if the languageId supplied is a shortLangPrefix mapped key on the languages object. */
const isShortLangPrefixKey = (languageId: string): languageId is Lucid.I18n.AvailableLanguagesKey => {
  const languageKeys = getLanguageData(languageId, 'shortLangPrefix') || {};

  return languageKeys && !!Object.keys(languageKeys).length;
};

/** Checks if the languageId supplied is a languageCode mapped key on the languages object. */
const isLanguageCode = (languageId: string): languageId is Lucid.I18n.AvailableLanguagesLanguageCode => {
  const languageKeys = getLanguageData(languageId, 'languageCode') || {};

  return languageKeys && !!Object.keys(languageKeys).length;
};

/**
 *
 * @param languageId
 * @returns `I18n.Language` `@default EN`
 */
const getLanguage = (languageId: string) => {
  if (isLanguageKey(languageId)) {
    return languages[languageId];
  }

  if (isLanguageI18nKey(languageId)) {
    const languageData = getLanguageData(languageId, 'i18nKey');
    return languageData;
  }

  if (isShortLangPrefixKey(languageId)) {
    const languageData = getLanguageData(languageId, 'shortLangPrefix');
    return languageData;
  }

  if (isLanguageCode(languageId)) {
    const languageData = getLanguageData(languageId, 'languageCode');
    return languageData;
  }

  return languages.en;
};

export default getLanguage;
